/**
 * Tipos para Auditoría — Fase 8
 *
 * Documentos relacionados:
 *   - DECISIONES.md #035 (triggers SQL + RPC log_audit_event)
 *   - requerimientos.md § 7.4 (operaciones a loguear)
 *
 * Las acciones son strings libres en la tabla (text not null) pero el sistema
 * usa estas convenciones:
 *   - CRUD por triggers: INSERT, UPDATE, DELETE (en MAYÚSCULAS, TG_OP de PG)
 *   - Eventos no-CRUD por RPC: LOGIN, LOGOUT, IMPERSONATE_START,
 *     IMPERSONATE_STOP, WEBHOOK_RECEIVED, PRINT_CUPON, etc.
 */

export interface AuditLog {
  id: string;
  usuario_id: string | null;
  usuario_real_id: string | null;
  accion: string;
  entidad: string | null;
  entidad_id: string | null;
  datos_anteriores: Record<string, unknown> | null;
  datos_nuevos: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  // Joins opcionales
  usuario?: { id: string; email: string; nombre: string; apellido: string | null } | null;
}

// ── Helpers de presentación ───────────────────────────────────────────────────

/** Devuelve un label legible en español para la acción. */
export function getAccionLabel(accion: string): string {
  const labels: Record<string, string> = {
    INSERT: 'Alta',
    UPDATE: 'Modificación',
    DELETE: 'Eliminación',
    LOGIN: 'Inicio de sesión',
    LOGOUT: 'Cierre de sesión',
    IMPERSONATE_START: 'Impersonación (inicio)',
    IMPERSONATE_STOP: 'Impersonación (fin)',
    WEBHOOK_RECEIVED: 'Webhook recibido',
    PASSWORD_CHANGED: 'Cambio de contraseña',
    PRINT_CUPON: 'Impresión de cupón',
  };
  return labels[accion] ?? accion;
}

export function getAccionColor(
  accion: string,
): 'success' | 'info' | 'warning' | 'error' | 'primary' | 'default' {
  switch (accion) {
    case 'INSERT':            return 'success';
    case 'UPDATE':            return 'info';
    case 'DELETE':            return 'error';
    case 'LOGIN':             return 'primary';
    case 'LOGOUT':            return 'default';
    case 'IMPERSONATE_START':
    case 'IMPERSONATE_STOP':  return 'warning';
    case 'WEBHOOK_RECEIVED':  return 'info';
    default:                  return 'default';
  }
}

/** Devuelve un label legible para la entidad (tabla de BD). */
export function getEntidadLabel(entidad: string | null): string {
  if (!entidad) return '—';
  const labels: Record<string, string> = {
    empresas:             'Empresas',
    afiliados:            'Afiliados',
    relaciones_laborales: 'Relaciones laborales',
    planillas_ddjj:       'DDJJ',
    cupones:              'Cupones',
    pagos:                'Pagos',
    conceptos_aporte:     'Conceptos de aporte',
    parametros:           'Parámetros',
    usuarios:             'Usuarios',
    transacciones_recaudacion: 'Transacciones',
  };
  return labels[entidad] ?? entidad;
}

// ── Filtros (server-side) ────────────────────────────────────────────────────

export interface AuditFiltros {
  accion?: string;
  entidad?: string;
  usuarioId?: string;
  desde?: string; // ISO date
  hasta?: string; // ISO date
  page?: number;
  pageSize?: number;
}

export interface AuditListResult {
  rows: AuditLog[];
  count: number;
  page: number;
  pageSize: number;
}
